import { Editor, Element as SlateElement, Transforms, Range, Node } from 'slate';
import { CustomText, CustomElement } from '../utils/element-renderer';

// Variables are inline elements that represent personalization tokens in SMS messages.
// Example: {{firstName}} or {{firstName | default: 'there'}}
export interface VariableElement {
  type: 'variable';
  variableName: string;
  defaultValue: string | null; // Optional fallback if the variable is undefined
  category: string; // Groups variables by type (e.g., 'contact', 'company')
  children: CustomText[]; // Required by Slate's architecture
}

// Type guard for variable elements, used in editor operations
function isVariable(n: Node): n is VariableElement {
  return SlateElement.isElement(n) && n.type === 'variable';
}

// Creates and inserts a new variable at the current selection.
// Variables are always void elements (no editable content).
export const insertVariable = (
  editor: Editor,
  variableName: string,
  category: string,
  defaultValue: string | null = null
) => {
  const variable: VariableElement = {
    type: 'variable',
    variableName,
    category,
    defaultValue,
    children: [{ text: '' }] // Required by Slate even though it's a void element
  };

  Transforms.insertNodes(editor, variable);
};

/**
 * Removes a variable element from the editor.
 * Uses fresh node references and handles race conditions.
 *
 * @param editor - The Slate editor instance
 * @param element - The variable element to remove
 * @returns boolean indicating if removal was successful
 */
export function removeVariable(editor: Editor, element: CustomElement): boolean {
  try {
    if (element.type !== 'variable') {
      return false;
    }

    // Force normalization first
    Editor.normalize(editor, { force: true });

    // Get fresh reference to ensure we have the latest state
    const nodeEntry = Editor.nodes(editor, {
      at: [],
      match: (n) =>
        SlateElement.isElement(n) &&
        n.type === 'variable' &&
        // Match by variableName and category since that uniquely identifies a variable
        'variableName' in n &&
        n.variableName === element.variableName &&
        'category' in n &&
        n.category === element.category
    }).next().value;

    if (nodeEntry) {
      const [_, path] = nodeEntry;
      Transforms.removeNodes(editor, { at: path });
      return true;
    }

    return false;
  } catch (err) {
    console.debug('Variable removal failed (path invalid or node missing)', err);
    return false;
  }
}

// Slate plugin that customizes editor behavior for variables.
// Key features:
// 1. Variables are inline elements (flow with text)
// 2. Variables are void (no editable content)
// 3. Custom backspace handling for better UX
export const withVariables = (editor: Editor) => {
  const { isInline, isVoid, deleteBackward } = editor;

  editor.isInline = (element: CustomElement) => {
    return element.type === 'variable' ? true : isInline(element);
  };

  editor.isVoid = (element: CustomElement) => {
    return element.type === 'variable' ? true : isVoid(element);
  };

  // Override backspace to delete the entire variable when the cursor
  // is at the start of it, rather than character by character
  editor.deleteBackward = (unit) => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [[node, _] = []] = Editor.nodes(editor, {
        match: isVariable,
        at: Range.start(selection)
      });

      if (node) {
        // Use removeVariable for consistent removal behavior
        removeVariable(editor, node as CustomElement);
        return;
      }
    }

    deleteBackward(unit);
  };

  return editor;
};
